import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from './authentication.service';
import { Observable, catchError, map, of, throwError } from 'rxjs';

const RESPONSE_PASSTHROUGH_PATHS = ['accesscontrol/Otp', 'accesscontrol/Account'];

/**
 * Returns true when response body should remain unchanged for the given URL.
 * @param url Response URL from HttpResponse.
 */
function shouldKeepOriginalResponseBody(url: string | null): boolean {
  if (!url) return false;
  return RESPONSE_PASSTHROUGH_PATHS.some((path) => url.includes(path));
}

/**
 * Normalizes API response payloads to body.response for standard backend responses.
 * @param event Interceptor pipeline event.
 */
function mapResponseEvent<T>(event: HttpEvent<T>): HttpEvent<T> {
  if (!(event instanceof HttpResponse)) return event;
  if (shouldKeepOriginalResponseBody(event.url ?? null)) return event;

  const body = event.body as { response?: T } | null;
  if (!body || !('response' in body)) return event;

  return event.clone({ body: body.response as T });
}

/**
 * Detects client/network-level request failures where server response is unavailable.
 * @param error HTTP error captured by interceptor.
 */
function isClientOrNetworkError(error: HttpErrorResponse): boolean {
  return error.error instanceof ErrorEvent || error.error instanceof ProgressEvent;
}

/**
 * Handles unauthorized responses by attempting refresh-token flow when possible.
 * @param authService Authentication service used for token lookup and refresh.
 * @param router Router used for fallback navigation.
 */
function handleUnauthorized(authService: AuthenticationService, router: Router): void {
  const accessToken = authService.getStoredAccessToken();
  const refreshToken = authService.getStoredRefreshToken();

  if (accessToken && refreshToken) {
    authService.getToken(refreshToken, true);
    return;
  }

  void router.navigate(['/']);
}

/**
 * Handles known HTTP status codes while preserving existing behavior.
 * @param error HTTP error response.
 * @param req Original request for context-only diagnostics.
 * @param authService Authentication service dependency.
 * @param router Router dependency.
 */
function handleHttpStatus(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  authService: AuthenticationService,
  router: Router
): Observable<HttpEvent<unknown>> | undefined {
  switch (error.status) {
    case 401:
      handleUnauthorized(authService, router);
      break;
    case 403:
      console.warn('Forbidden request:', req.url, error);
      break;
    case 404:
      if (req.url.includes('/HealthKpiSettings/getAlgorithmStatus/')|| req.url.includes('/conditionmonitoring/DeviceGateway/gateways')) {
        return of(
          new HttpResponse({
            status: 200,
            body: { response: null },
            url: error.url ?? undefined,
          })
        );
      }
      break;
    case 504:
      console.warn('Gateway timeout:', req.url, error);
      break;
    default:
      break;
  }

  return undefined;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthenticationService);

  return next(req).pipe(
    map(mapResponseEvent),
    catchError((error: HttpErrorResponse) => {
      if (isClientOrNetworkError(error)) {
        return throwError(() => new Error('No data'));
      }

      const handled = handleHttpStatus(error, req, authService, router);
      if (handled) {
        return handled;
      }

      return throwError(() => error);
    })
  );
};
